import { BsExclamationTriangleFill, BsFillExclamationCircleFill } from "react-icons/bs";
import { formatNumberWithSpaces } from "utils/formatNumberWithSpaces";
import useDashboardStore from "views/hooks/useDashboardStore";
import { MdOutlineKeyboardArrowRight } from "react-icons/md";
import useDashboardApi from "../hooks/useDashboardApi";
import useCoreStore from "views/hooks/useCoreStore";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { routes } from "config/routes";
import { IconType } from "react-icons";
import { Card, Col, Empty, Spin } from "antd";
import { useEffect } from "react";
import CardTitle from "components/CardTitle";
import { FaTasks } from "react-icons/fa";

interface RouteParams {
  companyId?: string;
}

interface FilterType {
  stage_In?: string[];
  state_In?: string[];
}

interface TaskItemProps {
  icon: IconType;
  color: string;
  title: string;
  totalDocuments?: number;
  totalAmount?: number;
  actionText: string;
  filter?: FilterType;
  type?: string;
  onClick: (filter: FilterType, type: string) => void;
}

export default function PriorityTasks() {
  const { t } = useTranslation();
  const urlParams: RouteParams = useParams();
  const { getPriorityTasks, priorityTasks, priorityTasksLoading: loading } = useDashboardApi();

  const setPriorityTasksInfo = useDashboardStore(state => state.setPriorityTasksInfo);
  const currency = useCoreStore(state => state.currency);

  const currencyCode = currency?.code;
  const decimalPlaces = currency?.decimalPlaces;

  useEffect(() => {
    if (urlParams?.companyId) {
      getPriorityTasks({
        variables: {
          companyId: urlParams?.companyId,
        },
      });
    }
  }, [urlParams?.companyId]);

  const handleOnClick = (filter: FilterType, type: string) => {
    setPriorityTasksInfo(filter);
    routes.navigate(`/company/${urlParams?.companyId}/documents/${type}`);
  };

  const TaskItem = ({
    icon,
    color,
    title,
    totalDocuments = 0,
    totalAmount = 0,
    actionText,
    filter,
    type,
    onClick,
  }: TaskItemProps) => {
    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && filter && type) {
        onClick(filter, type);
      }
    };

    return (
      <div
        className="task-item"
        onClick={() => filter && type && onClick(filter, type)}
        role="button"
        tabIndex={0}
        aria-label={`${totalDocuments} ${title} - ${actionText} ${formatNumberWithSpaces(
          Number(totalAmount).toFixed(decimalPlaces),
        )} ${currencyCode}`}
        onKeyDown={handleKeyDown}
      >
        <div className="task-icon" style={{ backgroundColor: `${color}15` }}>
          {icon({ size: 18, color })}
        </div>

        <div className="task-content">
          <div className="task-title">
            <strong>{totalDocuments}</strong> {title}
          </div>
          <div className="task-subtitle">
            {actionText}{" "}
            <strong>
              {formatNumberWithSpaces(Number(totalAmount).toFixed(decimalPlaces))} {currencyCode}
            </strong>{" "}
            <span className="tax-label">HT</span>
          </div>
        </div>

        <div className="task-arrow">
          <MdOutlineKeyboardArrowRight />
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="tasks-loading">
          <Spin size="large" />
        </div>
      );
    }

    const hasPriorityTasks =
      priorityTasks?.invoicesOverdue ||
      priorityTasks?.budgetsExpired ||
      priorityTasks?.budgetsAccepted;

    if (!hasPriorityTasks) {
      return (
        <div className="tasks-empty">
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("No priority tasks")} />
        </div>
      );
    }

    return (
      <div className="tasks-list">
        {priorityTasks?.invoicesOverdue && (
          <TaskItem
            filter={{ stage_In: ["overdue"] }}
            type="invoices"
            icon={BsExclamationTriangleFill}
            color="rgb(201, 39, 40)"
            title={t("late invoices")}
            totalDocuments={priorityTasks?.invoicesOverdue?.totalDocuments}
            totalAmount={priorityTasks?.invoicesOverdue?.totalAmount}
            actionText={t("For an amount of")}
            onClick={handleOnClick}
          />
        )}
        {priorityTasks?.budgetsExpired && (
          <TaskItem
            filter={{ stage_In: ["expired"] }}
            type="budget"
            icon={BsExclamationTriangleFill}
            color="rgb(201, 39, 40)"
            title={t("expired budgets")}
            totalDocuments={priorityTasks?.budgetsExpired?.totalDocuments}
            totalAmount={priorityTasks?.budgetsExpired?.totalAmount}
            actionText={t("For an amount of")}
            onClick={handleOnClick}
          />
        )}
        {priorityTasks?.budgetsAccepted && (
          <TaskItem
            filter={{ state_In: ["accepted"] }}
            type="budget"
            icon={BsFillExclamationCircleFill}
            color="#3e849e"
            title={t("budget accepted")}
            totalDocuments={priorityTasks?.budgetsAccepted?.totalDocuments}
            totalAmount={priorityTasks?.budgetsAccepted?.totalAmount}
            actionText={t("For an amount of")}
            onClick={handleOnClick}
          />
        )}
      </div>
    );
  };

  return (
    <Col md={12} sm={24} xs={24}>
      <Card
        className="antd-newrai-card tasks-card dashboard-custom-card"
        title={<CardTitle icon={<FaTasks />} title={t("Priority tasks")} />}
      >
        {renderContent()}
      </Card>
    </Col>
  );
}
